import { useCallback, useState } from 'react'
import type {
  SetupBotProtectionBootstrapInput,
  SetupEmailBootstrapInput,
  SetupOperationalPreferencesField,
  SetupOwnerField,
  SetupPaymentBootstrapInput,
  SetupStoreIdentityField,
  SetupValidationSummary,
  SetupWizardForm,
} from '../../types'
import { DEFAULT_SETUP_FORM } from './setupWizardDefaults'

export const useSetupWizardForm = () => {
  const [setupValidationSummary, setSetupValidationSummary] = useState<SetupValidationSummary | null>(null)
  const [setupForm, setSetupForm] = useState<SetupWizardForm>(DEFAULT_SETUP_FORM)

  const updateOwnerField = useCallback((field: SetupOwnerField, value: string) => {
    setSetupForm((prev) => ({
      ...prev,
      owner: {
        ...prev.owner,
        [field]: value,
      },
    }))
  }, [])

  const updateStoreIdentityField = useCallback((field: SetupStoreIdentityField, value: string) => {
    setSetupForm((prev) => ({
      ...prev,
      storeIdentity: {
        ...prev.storeIdentity,
        [field]: value,
      },
    }))
  }, [])

  const updateOnboardingPreferenceField = useCallback(
    <TField extends SetupOperationalPreferencesField>(
      field: TField,
      value: SetupWizardForm['onboardingPreferences'][TField],
    ) => {
      setSetupForm((prev) => ({
        ...prev,
        onboardingPreferences: {
          ...prev.onboardingPreferences,
          [field]: value,
        },
      }))
    },
    [],
  )

  const updatePaymentSetupField = useCallback(
    <TField extends keyof SetupPaymentBootstrapInput>(
      field: TField,
      value: SetupPaymentBootstrapInput[TField],
    ) => {
      setSetupForm((prev) => ({
        ...prev,
        paymentSetup: {
          ...prev.paymentSetup,
          [field]: value,
        },
      }))
    },
    [],
  )

  const updateEmailSetupField = useCallback(
    <TField extends keyof SetupEmailBootstrapInput>(
      field: TField,
      value: SetupEmailBootstrapInput[TField],
    ) => {
      setSetupForm((prev) => ({
        ...prev,
        emailSetup: {
          ...prev.emailSetup,
          [field]: value,
        },
      }))
    },
    [],
  )

  const updateBotProtectionSetupField = useCallback(
    <TField extends keyof SetupBotProtectionBootstrapInput>(
      field: TField,
      value: SetupBotProtectionBootstrapInput[TField],
    ) => {
      setSetupForm((prev) => ({
        ...prev,
        botProtectionSetup: {
          ...prev.botProtectionSetup,
          [field]: value,
        },
      }))
    },
    [],
  )

  const resetSetupForm = useCallback(() => {
    setSetupForm(DEFAULT_SETUP_FORM)
    setSetupValidationSummary(null)
  }, [])

  return {
    setupForm,
    setupValidationSummary,
    setSetupValidationSummary,
    updateOwnerField,
    updateStoreIdentityField,
    updateOnboardingPreferenceField,
    updatePaymentSetupField,
    updateEmailSetupField,
    updateBotProtectionSetupField,
    resetSetupForm,
  }
}
